import { useState } from 'react';

import { DashboardConfig } from '@/components/dashboard/DashboardConfig';
import { WIDGET_COMPONENTS } from '@/components/dashboard/widgets';
import { ExportPanel } from '@/components/export/ExportPanel';
import { Button } from '@/components/ui/Button';
import { useActiveOrg } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import {
  type WidgetId,
  WIDGET_CATALOG,
  loadEnabledWidgets,
} from '@/lib/dashboard';

export function DashboardPage() {
  const me = useMe();
  const org = useActiveOrg(me.data?.organizations);
  const [enabled, setEnabled] = useState<WidgetId[]>(() => loadEnabledWidgets());
  const [configOpen, setConfigOpen] = useState(false);

  // Render in catalog order so users get a consistent layout regardless
  // of the order they ticked checkboxes in. Filter out unknown IDs
  // defensively (handled in load() too, but if the user clears storage
  // mid-session this still holds).
  const enabledSet = new Set(enabled);
  const visible = WIDGET_CATALOG.filter((w) => enabledSet.has(w.id));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {org ? org.name : 'Your Workshop'}
          </h1>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
            {visible.length === 0
              ? 'Pick widgets to see what needs attention.'
              : `${visible.length} widget${visible.length === 1 ? '' : 's'} on the dashboard.`}
          </p>
        </div>
        <Button variant="secondary" onClick={() => setConfigOpen(true)}>
          ⚙ Configure dashboard
        </Button>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-stone-300 dark:border-stone-700 p-8 text-center text-stone-500 dark:text-stone-400">
          <p>No widgets selected.</p>
          <Button
            variant="primary"
            className="mt-3"
            onClick={() => setConfigOpen(true)}
          >
            Pick widgets
          </Button>
        </div>
      ) : (
        org?.slug && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {visible.map((w) => {
              const Component = WIDGET_COMPONENTS[w.id];
              return <Component key={w.id} orgSlug={org.slug} />;
            })}
          </div>
        )
      )}

      <ExportPanel orgSlug={org?.slug} />

      {configOpen && (
        <DashboardConfig
          initialEnabled={enabled}
          onSave={setEnabled}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}
